<template>
  <div class="adasiq-embed" :style="{ height }">
    <div v-if="!adasIqUrl" class="alert alert-secondary mb-0">ADAS IQ isn't set up on this environment.</div>
    <iframe v-else ref="frame" :key="frameKey" :src="src" title="ADAS IQ" class="adasiq-frame" allow="microphone"
      data-test="adasiq-frame"></iframe>
  </div>
</template>
<script>
import { mapState } from 'pinia'
import { authStore } from '@/stores/auth'
/*
  ADAS IQ, hosted by the ADAS IQ service and shown here in a frame (ADR-0018 in the ADAS IQ
  docs repo; the same component ADAS Map Pro uses). The page announces { type: 'adasiq:ready' };
  this component answers with the signed-in user's Connect Copilot token, which app it is,
  and (on a request's vehicle details) the job to pin: the ADAS Map inspection, the ADAS Map
  instance that holds it (source) and the VIN, all by postMessage to the service's origin,
  never through the URL. The service checks the token with its issuer and the Launchpad
  access flag itself. window.config.adasIqUrl names the service for this tier.
*/
export default {
  name: 'AdasIqEmbed',
  props: {
    // { adasInspection, source, vin, label } to pin the chat to a request; null for a free-standing chat.
    context: {
      type: Object,
      default: null
    },
    height: {
      type: String,
      default: '100%'
    }
  },
  emits: ['state'],
  computed: {
    ...mapState(authStore, ['token']),
    adasIqUrl() {
      return window.config && window.config.adasIqUrl ? String(window.config.adasIqUrl).replace(/\/+$/, '') : ''
    },
    adasIqOrigin() {
      try {
        return new URL(this.adasIqUrl).origin
      } catch (e) {
        return ''
      }
    },
    src() {
      return `${this.adasIqUrl}/embed/chat.html`
    },
    // A different job gets a fresh frame, so the page re-pins and re-picks its conversation.
    frameKey() {
      return this.context && this.context.adasInspection
        ? `job-${this.context.source || ''}-${this.context.adasInspection}`
        : 'global'
    }
  },
  watch: {
    frameKey(value, previous) {
      console.debug('[AdasIqEmbed] frame key', previous, '->', value)
    }
  },
  mounted() {
    console.debug('[AdasIqEmbed] mounted', this.frameKey)
    window.addEventListener('message', this.onMessage)
  },
  beforeUnmount() {
    console.debug('[AdasIqEmbed] unmounting', this.frameKey)
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    onMessage(event) {
      if (!this.adasIqOrigin || event.origin !== this.adasIqOrigin) return
      const frame = this.$refs.frame
      if (!frame || event.source !== frame.contentWindow) return
      const data = event.data
      if (!data || typeof data !== 'object') return
      if (data.type === 'adasiq:ready') {
        console.debug('[AdasIqEmbed] ready ->', 'init', this.frameKey)
        this.init()
      } else if (data.type === 'adasiq:state') {
        console.debug('[AdasIqEmbed] state', data.state, data.code || '')
        this.$emit('state', data)
      }
    },
    init() {
      if (!this.token) return
      this.post({
        type: 'adasiq:init',
        token: this.token,
        client: { app: 'connect-copilot', version: window.version ? String(window.version) : null },
        context: this.contextPayload()
      })
    },
    contextPayload() {
      if (!this.context || !this.context.adasInspection) return null
      return {
        adasInspection: parseInt(this.context.adasInspection),
        source: this.context.source || null,
        vin: this.context.vin || null,
        label: this.context.label || null
      }
    },
    post(message) {
      const frame = this.$refs.frame
      if (frame && frame.contentWindow && this.adasIqOrigin) {
        frame.contentWindow.postMessage(message, this.adasIqOrigin)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.adasiq-embed {
  width: 100%;
  min-height: 320px;
}
.adasiq-frame {
  width: 100%;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f5f6fa;
}
</style>
